export const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text
    return text.slice(0, maxLength) + '...'
}

interface FormatCompact3Options {
  integerUnder1000?: boolean
}

/**
 * Formats a number into a compact string with ~3 significant digits.
 *
 * Examples:
 * - 123 -> "123"
 * - 1_234 -> "1.23K"
 * - 12_340 -> "12.3K"
 * - 123_400 -> "123K"
 * - 1_234_567 -> "1.23M"
 */
export const formatCompact3 = (value: number, options: FormatCompact3Options = {}): string => {
  const integerUnder1000 = options.integerUnder1000 !== false
  const numberValue = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(numberValue)) return ''

  const sign = numberValue < 0 ? '-' : ''
  const abs = Math.abs(numberValue)

  if (abs < 1000) {
    return sign + String(integerUnder1000 ? Math.round(abs) : abs)
  }

  const UNITS = [
    { threshold: 1e12, suffix: 'T' },
    { threshold: 1e9, suffix: 'B' },
    { threshold: 1e6, suffix: 'M' },
    { threshold: 1e3, suffix: 'K' },
  ]

  const stripTrailingZeros = (input: string): string => input.replace(/\.0+$|(\.\d*[1-9])0+$/, '$1')

  const formatWithUnit = (unitIndex: number): string => {
    const { threshold, suffix } = UNITS[unitIndex]
    const scaled = abs / threshold

    const decimals = scaled < 10 ? 2 : scaled < 100 ? 1 : 0
    const rounded = Number(scaled.toFixed(decimals))

    if (rounded >= 1000 && unitIndex > 0) {
      return formatWithUnit(unitIndex - 1)
    }

    return `${sign}${stripTrailingZeros(rounded.toFixed(decimals))}${suffix}`
  }

  const initialIndex = UNITS.findIndex((unit) => abs >= unit.threshold)
  return formatWithUnit(initialIndex === -1 ? UNITS.length - 1 : initialIndex)
}

/**
 * Formats a date-like value as `m/d/yyyy` (US-style, no zero padding).
 */
export const formatDateMDY = (dateLike: Date | string | number): string => {
  const date = dateLike instanceof Date ? dateLike : new Date(dateLike)
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return ''
  const m = date.getMonth() + 1
  const d = date.getDate()
  const y = date.getFullYear()
  return `${m}/${d}/${y}`
}

/**
 * Always-relative age, for lists that are ordered by recency. `formatDate`
 * switches to absolute dates past a week, which stops being comparable at a
 * glance ('6/21/2026' vs '7/4/2026' is arithmetic, not reading).
 */
export const formatRelativeDate = (dateString: string): string => {
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return ''

  const now = new Date()
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const days = Math.round((startOfToday - startOfDate) / (1000 * 60 * 60 * 24))

  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days} days ago`
  if (days < 14) return 'Last week'
  if (days < 60) return `${Math.floor(days / 7)} weeks ago`
  if (days < 365) return `${Math.floor(days / 30)} months ago`
  const years = Math.floor(days / 365)
  return years === 1 ? 'Last year' : `${years} years ago`
}

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  if (Number.isNaN(date.getTime())) return ''

  const now = new Date()
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const dayMs = 1000 * 60 * 60 * 24
  const calendarDayDiff = Math.round((startOfToday - startOfDate) / dayMs)

  if (calendarDayDiff === 0) return 'Today'
  if (calendarDayDiff === 1) return 'Yesterday'
  if (calendarDayDiff >= 2 && calendarDayDiff <= 7) return `${calendarDayDiff} days ago`
  return date.toLocaleDateString()
}
